import { Flex } from '@chakra-ui/react'
import { setOptions, importLibrary } from '@googlemaps/js-api-loader'
import { KeyboardEvent, useEffect, useRef, useState } from 'react'
import { Location } from 'types'
import { getEventTimezone, sleep, useDisclosure } from 'utils'
import {
  DebouncedTextField,
  DebouncedTextFieldProps,
  MapPinIcon,
  Popover,
  PopoverProps,
  Text,
} from '.'

interface LocationWithId extends Location {
  placeId?: string
}

interface LocationSuggestion extends Omit<LocationWithId, 'lat' | 'lng' | 'timezone'> {}

export interface Props extends Omit<DebouncedTextFieldProps, 'onChange' | 'value'> {
  value?: LocationWithId
  popoverProps?: Omit<PopoverProps, 'children' | 'trigger'>
  onChange: (location?: Location) => void
}

export default function LocationAutocomplete({
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  popoverProps,
  value,
  ...rest
}: Props) {
  const getSuggestions = useGetSuggestions()
  const getPlaceDetailsAndTimezone = usePlaceDetailsAndTimezone()
  const { onOpen, onClose, open } = useDisclosure()
  const [search, setSearch] = useState(locationText(value))
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>()
  const [selectedIndex, setSelectedIndex] = useState<number>()

  useEffect(() => {
    setSearch(locationText(value))
  }, [value])

  const didSelect = useRef(false)

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!suggestions) return

    switch (e.key) {
      case 'ArrowUp': {
        e.preventDefault()
        setSelectedIndex(state => (!state ? suggestions.length - 1 : state - 1))
        break
      }
      case 'ArrowDown':
      case 'Tab': {
        e.preventDefault()
        setSelectedIndex(state => (state === suggestions.length - 1 ? 0 : (state ?? -1) + 1))
        break
      }
      case 'Enter': {
        const selected = selectedIndex != null ? suggestions?.[selectedIndex] : undefined
        if (selected) handleSelect(selected)
        break
      }
      case 'Escape': {
        e.preventDefault()
        handleCancel()
        break
      }
    }
  }

  function handleCancel() {
    setSearch(locationText(value))
    setSuggestions(undefined)
    onClose()
  }

  async function handleSelect(suggestion?: LocationSuggestion) {
    if (suggestion) {
      const locationWithDetails = await getPlaceDetailsAndTimezone(suggestion)
      if (!locationWithDetails) return
      didSelect.current = true
      setSearch(locationText(locationWithDetails))
      setSuggestions(undefined)
      onChange(locationWithDetails)
    } else {
      onChange()
    }
    onClose()
  }

  return (
    <Popover
      trigger={
        <DebouncedTextField
          controlled
          value={search}
          leftElement={<MapPinIcon type="outline" />}
          placeholder="Search for city.."
          autoComplete="off"
          onFocus={e => {
            e.target.select()
            onFocus?.(e)
          }}
          onBlur={async e => {
            // Wait to see if user clicked on a suggestion
            await sleep(250)
            if (didSelect.current) return (didSelect.current = false)

            if (search === '' && value) handleSelect()
            else handleCancel()

            onBlur?.(e)
          }}
          onChange={v => {
            onOpen()
            setSelectedIndex(undefined)
            setSearch(v)
            getSuggestions(v).then(setSuggestions)
          }}
          onKeyDown={e => {
            handleKeyDown(e)
            onKeyDown?.(e)
          }}
          {...rest}
        />
      }
      contentProps={{ borderRadius: 'xs' }}
      positioning={{ sameWidth: true, gutter: 8 }}
      onClose={onClose}
      hideClose
      closeOnInteractOutside
      open={open && Boolean(suggestions)}
      autoFocus={false}
      {...popoverProps}
    >
      <Flex direction="column">
        {suggestions?.length ? (
          suggestions.map((suggestion, index) => {
            const text = locationText(suggestion)

            return (
              <Flex
                key={text}
                align="center"
                gap={4}
                p={2}
                cursor="pointer"
                bg={index === selectedIndex ? 'gray.100' : 'transparent'}
                _hover={{ bg: 'gray.100' }}
                onClick={() => handleSelect(suggestion)}
              >
                <MapPinIcon type="outline" />
                <Text>{text}</Text>
              </Flex>
            )
          })
        ) : (
          <Text py={2} px={4} color="gray.500">
            No results found
          </Text>
        )}
      </Flex>
    </Popover>
  )
}

const { VITE_GOOGLE_MAPS_API } = import.meta.env

function useGetSuggestions() {
  const [api, setApi] = useState<google.maps.PlacesLibrary>()
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken>()

  useEffect(() => {
    if (!VITE_GOOGLE_MAPS_API)
      return console.error('Missing VITE_GOOGLE_MAPS_API in environment file')

    setOptions({
      key: VITE_GOOGLE_MAPS_API,
      v: 'weekly',
    })

    importLibrary('places').then(api => {
      setApi(api)
      setSessionToken(new api.AutocompleteSessionToken())
    })
  }, [])

  async function getSuggestions(input?: string) {
    if (!api || !sessionToken || !input) return

    const request: google.maps.places.AutocompleteRequest = {
      includedPrimaryTypes: ['locality'],
      input,
      language: 'en-US',
      sessionToken,
    }

    const { suggestions } =
      (await api.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)) || {}
    if (!suggestions) return

    return suggestions.map(({ placePrediction }) => {
      const { mainText, secondaryText } = placePrediction || {}
      const placeId = placePrediction?.placeId
      const city = mainText?.toString() ?? ''
      const secondary = secondaryText?.toString() ?? ''
      const [state, country] = secondary.split(', ')
      return country ? { city, country, state, placeId } : { city, country: state, placeId }
    })
  }

  return getSuggestions
}

function usePlaceDetailsAndTimezone() {
  const [api, setApi] = useState<google.maps.PlacesLibrary>()

  useEffect(() => {
    if (!VITE_GOOGLE_MAPS_API) {
      console.error('Missing VITE_GOOGLE_MAPS_API in environment file')
      return
    }

    setOptions({
      key: VITE_GOOGLE_MAPS_API,
      v: 'weekly',
    })

    importLibrary('places').then(api => setApi(api))
  }, [])

  async function getPlaceDetailsAndTimezone(
    location: LocationSuggestion
  ): Promise<Location | null> {
    if (!api || !location.placeId) return null

    try {
      const place = new google.maps.places.Place({
        id: location.placeId,
      })
      await place.fetchFields({
        fields: ['location', 'addressComponents'],
      })
      const latLng = place.location
      if (!latLng) throw new Error('No location returned from place')

      const lat = latLng.lat()
      const lng = latLng.lng()

      // Extract ISO codes from address components
      const components = place.addressComponents
      let state = location.state
      let country = location.country
      if (components) {
        const stateComponent = components.find(c => c.types.includes('administrative_area_level_1'))
        const countryComponent = components.find(c => c.types.includes('country'))
        state = stateComponent?.shortText || stateComponent?.longText || state
        country = countryComponent?.shortText || countryComponent?.longText || country
      }

      const timezoneResult = await getEventTimezone({
        lat,
        lng,
        timestamp: Math.floor(Date.now() / 1000),
      })

      if (!timezoneResult) throw new Error('No timezone information returned from place')

      return {
        city: location.city,
        country,
        lat,
        lng,
        state,
        timezone: timezoneResult?.data,
      }
    } catch (error) {
      console.error('Error fetching place details or timezone:', error)
      return null
    }
  }

  return getPlaceDetailsAndTimezone
}

const locationText = (l?: LocationSuggestion) =>
  l?.city ? `${l.city}, ${l.state || l.country}` : ''
